// src/modules/Home/styles.js
// VEGA ROBOTICS - Home Styles (Paleta Vega)
import styled, { keyframes } from 'styled-components';

// ============================================================
// DESIGN TOKENS - PALETA VEGA
// ============================================================
export const colors = {
  // Backgrounds
  bgWhite: '#FFFFFF',
  bgLight: '#F5F5F5',
  bgDark: '#111111',
  bgFooter: '#111111',
  
  // Primary brand (botões - NÃO usar vermelho!)
  primary: '#111111',
  primaryDark: '#2A2A2A',
  primaryLight: '#333333',
  
  // Accent (hovers, tags, destaques)
  vegaRed: '#9D0008',
  
  // Text on light backgrounds
  textDark: '#111111',
  textBody: '#333333',
  textMuted: '#666666',
  textLight: '#999999',
  
  // Text on dark backgrounds
  textWhite: '#FFFFFF',
  textWhiteSecondary: 'rgba(255, 255, 255, 0.7)',
  textWhiteMuted: 'rgba(255, 255, 255, 0.5)',
  
  // Borders
  border: 'rgba(0, 0, 0, 0.08)',
  borderMedium: 'rgba(0, 0, 0, 0.12)',
  borderDark: 'rgba(255, 255, 255, 0.1)',
  
  // Shadows
  cardShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  cardShadowHover: '0 8px 24px rgba(0, 0, 0, 0.1)',
  
  // Accents
  star: '#F5A623',
  nvidia: '#76B900',
};

// ============================================================
// KEYFRAMES
// ============================================================
export const slideIn = keyframes`
  0% { opacity: 0; transform: translateX(-15px); }
  100% { opacity: 1; transform: translateX(0); }
`;

export const slideOut = keyframes`
  0% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(15px); }
`;

export const scrollLeft = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// ============================================================
// LAYOUT BASE
// ============================================================
export const PortalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: ${colors.bgWhite};
  color: ${colors.textDark};
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
`;

export const Main = styled.main`
  flex: 1;
`;

// ============================================================
// HERO SECTION (Dark - mantém impacto do vídeo)
// ============================================================
export const HeroSection = styled.section`
  position: relative;
  height: 70vh;
  min-height: 500px;
  max-height: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${colors.bgDark};
  
  @media (max-width: 768px) {
    height: 60vh;
    min-height: 400px;
    max-height: 550px;
  }
`;

export const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg, 
    rgba(0, 0, 0, 0.85) 0%, 
    rgba(0, 0, 0, 0.6) 50%, 
    rgba(0, 0, 0, 0.4) 100%
  );
`;

export const HeroContent = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  max-width: 1400px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0 48px;
  top: 0;
  bottom: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) { 
    padding: 0 24px;
    bottom: 80px;
  }
`;

export const HeroEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.textWhiteMuted};
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 11px;
    margin-bottom: 12px;
  }
`;

export const HeroTitle = styled.h1`
  font-size: clamp(24px, 3vw, 36px);
  font-weight: 300;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: ${colors.textWhite};
  text-shadow: 0 2px 30px rgba(0, 0, 0, 0.4);
  max-width: 800px;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.4;
    max-width: 100%;
  }

  @media (max-width: 380px) {
    font-size: 14px;
  }
`;

export const StaticText = styled.span`
  display: inline;
  color: ${colors.textWhiteSecondary};
`;

export const DynamicWrapper = styled.span`
  display: inline-block;
  position: relative;
`;

export const DynamicText = styled.span`
  display: inline-block;
  color: ${colors.textWhite};
  animation: ${slideIn} 2s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
`;

export const ExitingText = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  display: inline-block;
  color: ${colors.textWhite};
  animation: ${slideOut} 1.6s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
  pointer-events: none;
`;

export const HeroButtonWrapper = styled.div`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  
  @media (max-width: 640px) {
    bottom: 48px;
  }
`;

export const HeroButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: ${colors.textWhite};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.5px;
  background: transparent;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: ${colors.bgWhite};
    color: ${colors.textDark};
  }

  @media (max-width: 480px) {
    padding: 14px 28px;
    font-size: 14px;
    min-width: 220px;
  }
`;

export const HeroControls = styled.div`
  position: absolute;
  bottom: 24px;
  right: 48px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 640px) { 
    right: 24px; 
    bottom: 16px; 
  }
`;

export const ControlButton = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textWhite};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0.7;

  &:hover { opacity: 1; }

  &:active {
    opacity: 1;
    transform: scale(0.95);
  }

  svg { width: 18px; height: 18px; }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    svg { width: 20px; height: 20px; }
  }
`;

// ============================================================
// CLIENTS SECTION (Light - logos escuros)
// ============================================================
export const ClientsSection = styled.section`
  background: ${colors.bgWhite};
  padding: 48px 0;
  overflow: hidden;
  border-bottom: 1px solid ${colors.border};
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

export const ClientsContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

export const ClientsCarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  
  /* Fade nas extremidades - esticado até o fim */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 250px;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(90deg, ${colors.bgWhite} 0%, ${colors.bgWhite} 20%, transparent 100%);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(90deg, transparent 0%, ${colors.bgWhite} 80%, ${colors.bgWhite} 100%);
  }
  
  @media (max-width: 1024px) {
    &::before, &::after { width: 150px; }
  }
  
  @media (max-width: 768px) {
    &::before, &::after { width: 80px; }
  }
`;

export const ClientsCarouselTrack = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
  animation: ${scrollLeft} 40s linear infinite;
  
  &:hover { animation-play-state: paused; }
`;

export const ClientLogoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  
  @media (max-width: 768px) { padding: 0 32px; }
`;

export const ClientLogoImage = styled.img`
  height: 40px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  filter: grayscale(100%);
  opacity: 0.5;
  transition: all 0.3s ease;
  
  &:hover {
    filter: grayscale(0%);
    opacity: 1;
  }
  
  @media (max-width: 768px) { 
    height: 32px; 
    max-width: 120px;
  }
`;

export const ClientLogoPlaceholder = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.textDark};
  opacity: 0.3;
  white-space: nowrap;
  letter-spacing: 0.5px;
  transition: opacity 0.3s ease;
  
  &:hover { opacity: 0.6; }
`;

// ============================================================
// SOLUTIONS SECTION (Light alternado)
// ============================================================
export const SolutionsSection = styled.section`
  background: ${colors.bgLight};
  padding: 100px 48px;
  
  @media (max-width: 768px) {
    padding: 70px 24px;
  }
`;

export const SolutionsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

export const SolutionsHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) { margin-bottom: 48px; }
`;

export const SolutionsTitle = styled.h2`
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 300;
  color: ${colors.textDark};
  letter-spacing: -0.01em;
`;

export const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const SolutionCard = styled.a`
  background: ${colors.bgWhite};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 32px 28px;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  box-shadow: ${colors.cardShadow};
  
  &:hover {
    border-color: ${colors.vegaRed};
    transform: translateY(-4px);
    box-shadow: ${colors.cardShadowHover};
  }
`;

export const SolutionTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: ${colors.textDark};
  margin-bottom: 12px;
`;

export const SolutionDescription = styled.p`
  font-size: 14px;
  font-weight: 300;
  color: ${colors.textMuted};
  line-height: 1.6;
  margin-bottom: auto;
  flex: 1;
`;

export const SolutionLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.textMuted};
  margin-top: 20px;
  transition: color 0.3s ease;
  
  ${SolutionCard}:hover & {
    color: ${colors.vegaRed};
  }
`;

// ============================================================
// TESTIMONIALS SECTION (Light - branco puro)
// ============================================================
export const TestimonialsSection = styled.section`
  background: ${colors.bgWhite};
  padding: 100px 48px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 70px 24px;
  }
`;

export const TestimonialsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

export const TestimonialsHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) { margin-bottom: 48px; }
`;

export const TestimonialsTitle = styled.h2`
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 300;
  color: ${colors.textDark};
  letter-spacing: -0.01em;
`;

export const TestimonialsCarouselWrapper = styled.div`
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 60px;

  @media (max-width: 1024px) {
    padding: 0 50px;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`;

export const TestimonialsCarouselTrack = styled.div`
  overflow: hidden;
  width: 100%;
`;

export const TestimonialsCarouselSlider = styled.div`
  display: flex;
  gap: 24px;
  transition: transform 0.5s ease;
  transform: translateX(${props => props.$offset}%);

  @media (max-width: 768px) {
    transform: translateX(${props => props.$mobileOffset}%);
    gap: 16px;
    padding: 0 16px;
  }
`;

export const TestimonialCard = styled.div`
  flex: 0 0 calc((100% - 48px) / 3);
  min-width: 300px;
  background: ${colors.bgWhite};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 32px;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: ${colors.cardShadow};

  &:hover {
    border-color: ${colors.borderMedium};
    box-shadow: ${colors.cardShadowHover};
  }

  @media (max-width: 1024px) {
    flex: 0 0 calc((100% - 24px) / 2);
  }

  @media (max-width: 768px) {
    flex: 0 0 calc(100% - 32px);
    min-width: calc(100% - 32px);
    padding: 24px;
  }
`;

export const TestimonialCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const TestimonialAuthorPhoto = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const TestimonialAuthorInfo = styled.div`
  flex: 1;
`;

export const TestimonialAuthorName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${colors.textDark};
  margin-bottom: 4px;
`;

export const TestimonialStarsRow = styled.div`
  display: flex;
  gap: 2px;
  
  svg {
    color: ${colors.star};
    font-size: 12px;
  }
`;

export const TestimonialText = styled.p`
  font-size: 14px;
  font-weight: 300;
  color: ${colors.textMuted};
  line-height: 1.7;
  margin: 0;
`;

export const TestimonialsCarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid ${colors.border};
  background: ${colors.bgWhite};
  color: ${colors.textDark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: ${colors.cardShadow};
  
  &:hover:not(:disabled) {
    border-color: ${colors.textDark};
    background: ${colors.textDark};
    color: ${colors.bgWhite};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  &.prev { left: 0; }
  &.next { right: 0; }
  
  svg { font-size: 14px; }
  
  @media (max-width: 1024px) { display: none; }
`;

export const TestimonialsCarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-top: 32px;
`;

export const TestimonialsDot = styled.button`
  width: 12px;
  height: 12px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 16px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background: ${props => props.$active ? colors.textDark : colors.border};
    border-radius: 50%;
    transition: all 0.3s ease;
  }

  &:hover::after {
    background: ${props => props.$active ? colors.textDark : colors.borderMedium};
    transform: scale(1.2);
  }

  &:active::after {
    transform: scale(0.9);
  }
`;

// ============================================================
// CTA SECTION (Dark - contraste para conversão)
// ============================================================
export const CTASection = styled.section`
  background: ${colors.bgDark};
  padding: 120px 48px;
  
  @media (max-width: 768px) {
    padding: 80px 24px;
  }
`;

export const CTAContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

export const CTATitle = styled.h2`
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 600;
  color: ${colors.textWhite};
  margin-bottom: 16px;
  letter-spacing: -0.02em;
`;

export const CTASubtitle = styled.p`
  font-size: clamp(16px, 2vw, 18px);
  font-weight: 300;
  color: ${colors.textWhiteSecondary};
  margin-bottom: 48px;
`;

export const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 48px;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const CTAButtonPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  background: ${colors.bgWhite};
  color: ${colors.textDark};
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
  }
  
  svg { font-size: 16px; }
  
  @media (max-width: 640px) {
    width: 100%;
    max-width: 280px;
  }
`;

export const CTAButtonSecondary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: ${colors.textWhite};
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }
  
  svg { font-size: 16px; }
  
  @media (max-width: 640px) {
    width: 100%;
    max-width: 280px;
  }
`;

export const CTAContacts = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const CTAContactItem = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.textWhiteMuted};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover { color: ${colors.textWhite}; }
  svg { font-size: 14px; }
`;

// ============================================================
// BLOG SECTION (Light alternado)
// ============================================================
export const BlogSection = styled.section`
  background: ${colors.bgLight};
  padding: 100px 48px;
  
  @media (max-width: 768px) {
    padding: 70px 24px;
  }
`;

export const BlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

export const BlogHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) { margin-bottom: 48px; }
`;

export const BlogTitle = styled.h2`
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 300;
  color: ${colors.textDark};
  margin-bottom: 12px;
  letter-spacing: -0.01em;
`;

export const BlogSubtitle = styled.p`
  font-size: clamp(14px, 2vw, 16px);
  font-weight: 300;
  color: ${colors.textMuted};
`;

export const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 48px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 16px;
    margin: 0 -24px 32px -24px;
    padding: 0 24px 16px 24px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    & > * {
      flex: 0 0 85%;
      max-width: 85%;
      scroll-snap-align: center;
    }
  }
`;

export const BlogCard = styled.a`
  background: ${colors.bgWhite};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: ${colors.cardShadow};
  
  &:hover {
    border-color: ${colors.vegaRed};
    transform: translateY(-4px);
    box-shadow: ${colors.cardShadowHover};
  }
`;

export const BlogCardImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${colors.bgLight};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  ${BlogCard}:hover & img {
    transform: scale(1.05);
  }
`;

export const BlogCardContent = styled.div`
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const BlogCardCategory = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.vegaRed};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

export const BlogCardTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: ${colors.textDark};
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const BlogCardExcerpt = styled.p`
  font-size: 14px;
  font-weight: 300;
  color: ${colors.textMuted};
  line-height: 1.6;
  margin-bottom: 16px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const BlogCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: ${colors.textLight};
`;

export const BlogCardMetaDivider = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${colors.textLight};
`;

export const BlogButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const BlogButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  border: 1px solid ${colors.borderMedium};
  color: ${colors.textDark};
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${colors.textDark};
    border-color: ${colors.textDark};
    color: ${colors.bgWhite};
  }
  
  svg { font-size: 14px; }
`;

// ============================================================
// WHATSAPP FLUTUANTE
// ============================================================
export const WhatsAppFloat = styled.a`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: #25D366;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textWhite};
  font-size: 28px;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
  z-index: 999;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(37, 211, 102, 0.5);
  }
  
  @media (max-width: 640px) {
    bottom: 16px;
    right: 16px;
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
`;

// ============================================================
// EXPORTS
// ============================================================
export default { colors };
